// Per-game "Play off Low" handicap adjustment.
//
// When a game's handicap mode is Play off Low, every player's handicap
// index is reduced by the lowest index in the round so the lowest
// player plays off 0 and the rest carry the delta.
// Example: 10 / 12 / 14 / 16 → 0 / 2 / 4 / 6.
//
// The stored Player.handicap is never mutated. This helper returns a
// new Player array with adjusted values so a runner can swap it into
// any place that already reads p.handicap without rewriting the
// downstream logic. The HandicapMode option lives on each game that
// opts into the adjustment (Stableford, Hi-Lo), so different games in
// the same round can run with different policies.

import { Player } from "./types";

export type HandicapMode = "full" | "playOffLow";

export function adjustHandicaps(
  players: Player[],
  mode: HandicapMode | undefined,
): Player[] {
  if (mode !== "playOffLow") return players;
  const indices = players
    .map((p) => p.handicap)
    .filter((h): h is number => typeof h === "number");
  if (indices.length === 0) return players;
  const min = Math.min(...indices);
  if (min === 0) return players; // already anchored — no work to do
  return players.map((p) =>
    typeof p.handicap === "number"
      ? { ...p, handicap: p.handicap - min }
      : p,
  );
}
